// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import * as L from 'leaflet';
import G from 'google-map-react';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { removeTab } from 'wdna-components/lib/tabs';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import MapModal from 'wdna-components/lib/modals/generics/MapModal';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { location, LocationDO } from '../../requests';

import ButtonsForm from '../../../../components/ButtonsForm';
import Form from './Form';

interface Props {
  rowId: number;
  notify?: () => void;
}

interface State {
  loading: boolean;
  showMap: boolean;

  data: Partial<LocationDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};

    this.state = {
      loading: false,
      showMap: false,

      data: {},
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    const { rowId } = this.props;
    const isNew: boolean = !!isNaN(rowId);

    this.loadingStart();
    try {
      let rowData: Partial<LocationDO> = this.state.data || {};
      if (!isNew) {
        rowData = await location<Partial<LocationDO>>('get', String(rowId)) || {};
      }
      this.setLocalState(
        {
          data: { ...rowData, },
        },
        () => {
          this.defaultValues = _.cloneDeep(rowData);
        }
      );
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public onReset = (): void => {
    this.setLocalState({ data: { ...this.defaultValues } });
  };

  public submit = (event: React.FormEvent<HTMLFormElement>): any => {
    event.preventDefault();
    const { data } = this.state;

    this.loadingStart();
    location('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Location successfully ${data?.id ? 'edited' : 'created'} ${data.name}`, 5000);
        const { notify } = this.props;
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('LE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleMapClick = (e: L.LeafletMouseEvent | G.ClickEventValue): void => {
    const lat: number = (e as G.ClickEventValue).lat || (e as L.LeafletMouseEvent).latlng.lat;
    const lng: number = (e as G.ClickEventValue).lng || (e as L.LeafletMouseEvent).latlng.lng;
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        latitude: lat,
        longitude: lng,
      },
    }));
  };

  public showMap = (): void => {
    this.setLocalState({ showMap: true });
  };

  public closeMap = (): void => {
    this.setLocalState({ showMap: false });
  };

  public loadingStart = (): void => {
    this.setLocalState({ loading: true });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setLocalState({ loading: false });
    window.store.dispatch(finishProcess(this.pid));
  };

  public render(): JSX.Element {
    const { data, loading, showMap } = this.state;

    let mapZoom: number = 6;
    let mapInitialMapCenter: [number, number] = [40.6, 1.13];
    if (_.isNumber(data?.latitude) && _.isNumber(data?.longitude)) {
      mapZoom = 10;
      mapInitialMapCenter = [data.latitude, data.longitude];
    }

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Location {data?.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form
                        formValues={this.state}
                        onChange={this.handleChange}
                        showMap={this.showMap}
                      />
                      <MapModal
                        zoom={mapZoom}
                        initialMapCenter={mapInitialMapCenter}
                        latitude={data?.latitude || 0}
                        longitude={data?.longitude || 0}
                        onMapClick={this.handleMapClick}
                        title="Select coordinates of the location"
                        show={showMap}
                        onCancel={this.closeMap}
                        useGoogleMaps={false}
                      />
                      <ButtonsForm loading={loading} disabled={!hasLevel('role', ['ADM', 'URW'])} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
